import { fetchEvent } from "api"
import { verifyToken } from "api/auth/verifyToken"
import { dehydrate, QueryClient } from "@tanstack/react-query"
import { queryKeys } from "utils/queryKeys"

const EVENT_ID_ALIASES: Record<string, string | undefined> = {
    "3x3jeddah": "6564a239d57a803c355ea0b0",
    f1: process.env.NEXT_PUBLIC_CURRENT_F1_ID,
    f: process.env.NEXT_PUBLIC_CURRENT_F1_ID,
}

export type EventPageServerProps = {
    appUserToken?: string | null
    isWebView?: boolean
    dehydratedState?: unknown
}

export function resolveEventRouteId(eventId?: string): string {
    if (!eventId) return ""
    return EVENT_ID_ALIASES[eventId] || eventId
}

export function getCanonicalUrl(asPath: string): string {
    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath = asPath.split("#")[0].split("?")[0] || "/"
    return `${baseUrl}${currentPath}`
}

export function getLanguageAlternates(asPath: string) {
    const baseUrl = (
        process.env.NEXT_PUBLIC_SITE_URL || "https://pass-tickets.com"
    ).replace(/\/$/, "")
    const currentPath = asPath.split("#")[0].split("?")[0] || "/"
    const pathNoLocale = currentPath.replace(/^\/(en-US|ar-SA)(?=\/|$)/, "")
    const defaultHref = `${baseUrl}${pathNoLocale || "/"}`
    const enHref = `${baseUrl}/en-US${pathNoLocale || "/"}`

    return [
        { hrefLang: "x-default", href: defaultHref },
        { hrefLang: "ar", href: defaultHref },
        { hrefLang: "en", href: enHref },
    ]
}

export async function getEventPageServerSideProps({
    req,
    params,
    requireVerifiedWebViewToken = false,
}: {
    req: {
        headers: {
            origin?: string
            token?: string
        }
    }
    params?: {
        eventid?: string
    }
    requireVerifiedWebViewToken?: boolean
}) {
    try {
        const queryClient = new QueryClient()
        const isWebView = req.headers.origin === "mobile_app"
        let appUserToken: string | null = null

        if (isWebView && requireVerifiedWebViewToken) {
            appUserToken = req.headers.token || null

            if (!appUserToken) {
                return { notFound: true }
            }

            const response = await verifyToken(appUserToken)

            if (!response?.success) {
                return { notFound: true }
            }
        }

        const rawEventId = params?.eventid
        const eventId = resolveEventRouteId(rawEventId)

        if (eventId) {
            try {
                await queryClient.prefetchQuery({
                    queryKey: queryKeys.event(eventId),
                    queryFn: () => fetchEvent(eventId),
                })
            } catch (prefetchError) {
                console.error(
                    "Error prefetching event data on server:",
                    prefetchError
                )
            }
        }

        return {
            props: {
                appUserToken,
                isWebView,
                dehydratedState: dehydrate(queryClient),
            } satisfies EventPageServerProps,
        }
    } catch (error) {
        console.error(error)
        return { notFound: true }
    }
}
